import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Typography, 
  Form, 
  Space, 
  message, 
  Tabs, 
  Row,
  Col,
  Statistic,
  Divider,
  Progress,
  Tag,
  List,
  Avatar,
  Badge,
  Tooltip,
  Alert,
  Collapse,
  Timeline,
  Select,
  Input,
  DatePicker,
  Popconfirm,
  Table,
  Steps,
  Empty
} from 'antd';
import { 
  AimOutlined, 
  PlusOutlined,
  ArrowLeftOutlined,
  TrophyOutlined,
  StarOutlined,
  FireOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
  BarChartOutlined,
  BookOutlined,
  BulbOutlined,
  InfoCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { goalsConfig } from '../config/goals';
import axios from 'axios';
import dayjs from 'dayjs';

// 配置axios基础URL
axios.defaults.baseURL = (typeof window !== 'undefined' ? window.location.protocol + '//' + window.location.host : '') + '/api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;
const { Option } = Select;
const { Step } = Steps;

const Goals = () => {
  const navigate = useNavigate();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showTipsModal, setShowTipsModal] = useState(false);
  const [activeTab, setActiveTab] = useState('goals');
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [goalForm] = Form.useForm();
  const [progressForm] = Form.useForm();
  const [typeForm] = Form.useForm();

  // 加载目标数据
  const fetchGoals = async () => {
    setLoading(true);
    try {
      const response = await axios.get(goalsConfig.apiEndpoints.goals);
      if (response.data.success) {
        setGoals(response.data.goals || []);
      } else {
        message.error(response.data.message || '获取目标列表失败');
      }
    } catch (error) {
      console.error('获取目标列表失败:', error);
      message.error('获取目标列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 打开目标弹窗
  const handleOpenGoal = (goal = null) => {
    setSelectedGoal(goal);
    if (goal) {
      // 编辑现有目标
      goalForm.setFieldsValue({
        title: goal.title,
        description: goal.description,
        type: goal.type,
        status: goal.status,
        priority: goal.priority,
        start_date: goal.start_date ? dayjs(goal.start_date) : null,
        end_date: goal.end_date ? dayjs(goal.end_date) : null,
        target_value: goal.target_value,
        current_value: goal.current_value,
        plan: goal.plan,
        evaluation: goal.evaluation,
        reward: goal.reward
      });
    } else {
      // 新建目标
      goalForm.resetFields();
    }
    setShowGoalModal(true);
  };

  // 保存目标
  const handleSaveGoal = async (values) => {
    try {
      const url = selectedGoal 
        ? `${goalsConfig.apiEndpoints.update}/${selectedGoal.id}`
        : goalsConfig.apiEndpoints.create;
      
      const method = selectedGoal ? 'PUT' : 'POST';
      const token = localStorage.getItem('token') || (localStorage.getItem('auth-storage') ? JSON.parse(localStorage.getItem('auth-storage')).state.token : '');
      
      const requestBody = {
        // user_id 现在从JWT token中获取，不需要在请求中传递
        title: values.title,
        description: values.description,
        type: values.type,
        status: values.status,
        priority: values.priority,
        start_date: values.start_date ? (dayjs.isDayjs(values.start_date) ? values.start_date.format('YYYY-MM-DD') : values.start_date) : null,
        end_date: values.end_date ? (dayjs.isDayjs(values.end_date) ? values.end_date.format('YYYY-MM-DD') : values.end_date) : null,
        target_value: values.target_value,
        current_value: values.current_value || 0,
        plan: values.plan,
        evaluation: values.evaluation,
        reward: values.reward
      };
      
      let response;
      if (selectedGoal) {
        response = await axios.put(`${goalsConfig.apiEndpoints.update}/${selectedGoal.id}`, requestBody);
      } else {
        response = await axios.post(goalsConfig.apiEndpoints.create, requestBody);
      }

      if (response.data.success) {
        message.success(selectedGoal ? '目标更新成功' : '目标创建成功');
        setShowGoalModal(false);
        fetchGoals();
      } else {
        message.error(response.data.message || '操作失败');
      }
    } catch (error) {
      console.error('保存目标失败:', error);
      message.error('操作失败');
    }
  };

  // 删除目标
  const handleDeleteGoal = async (goalId) => {
    try {
      const response = await axios.delete(`${goalsConfig.apiEndpoints.delete}/${goalId}`);
      if (response.data.success) {
        message.success('目标删除成功');
        fetchGoals();
      } else {
        message.error(response.data.message || '删除失败');
      }
    } catch (error) {
      console.error('删除目标失败:', error);
      message.error('删除失败');
    }
  };

  // 打开进度弹窗
  const handleOpenProgress = (goal) => {
    setSelectedGoal(goal);
    progressForm.resetFields();
    setShowProgressModal(true);
  };

  // 保存进度
  const handleSaveProgress = async (values) => {
    try {
      const requestBody = {
        goal_id: selectedGoal.id,
        content: values.content,
        percentage: values.percentage,
        notes: values.notes
      };
      
      const response = await axios.post(goalsConfig.apiEndpoints.addProgress, requestBody);

      if (response.data.success) {
        message.success('进度记录成功');
        setShowProgressModal(false);
        fetchGoals();
      } else {
        message.error(response.data.message || '操作失败');
      }
    } catch (error) {
      console.error('保存进度失败:', error);
      message.error('操作失败');
    }
  };

  // 计算目标完成率
  const calculateProgress = (goal) => {
    if (!goal.target_value || goal.target_value === 0) return 0;
    return Math.round((goal.current_value / goal.target_value) * 100);
  };

  // 获取目标状态颜色
  const getStatusColor = (status) => {
    const statusItem = goalsConfig.goalStatus.find(s => s.key === status);
    return statusItem ? statusItem.color : '#D9D9D9';
  };

  // 获取目标类型颜色
  const getTypeColor = (type) => {
    const typeItem = goalsConfig.goalTypes.find(t => t.key === type);
    return typeItem ? typeItem.color : '#D9D9D9';
  };

  // 获取优先级颜色
  const getPriorityColor = (priority) => {
    const priorityItem = goalsConfig.priorities.find(p => p.key === priority);
    return priorityItem ? priorityItem.color : '#D9D9D9';
  };

  // 组件加载时获取数据
  useEffect(() => {
    fetchGoals();
  }, []);

  // 统计概览
  const totalGoals = goals.length;
  const activeGoals = goals.filter(g => g.status === 'active').length;
  const completedGoals = goals.filter(g => g.status === 'completed').length;
  const planningGoals = goals.filter(g => g.status === 'planning').length;

  return (
    <div className="mod-page">
      {/* 头部导航 */}
      <div className="mod-header">
        <div>
          <div className="mod-header-title">
            <AimOutlined style={{ marginRight: '8px', color: goalsConfig.bgColor }} />
            {goalsConfig.name}
          </div>
          <div className="mod-header-subtitle">
            设定、分解、执行、监督 —— 用 SMART 原则达成每一个目标
          </div>
        </div>
        <Space wrap>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/')}
          >
            返回
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleOpenGoal()}
            style={{ backgroundColor: goalsConfig.bgColor, borderColor: goalsConfig.bgColor }}
          >
            新增目标
          </Button>
          <Button
            icon={<SettingOutlined />}
            onClick={() => setShowTypeModal(true)}
            style={{ borderColor: goalsConfig.bgColor, color: goalsConfig.bgColor }}
          >
            目标类型
          </Button>
          <Button
            icon={<InfoCircleOutlined />}
            onClick={() => setShowTipsModal(true)}
            style={{ borderColor: goalsConfig.bgColor, color: goalsConfig.bgColor }}
          >
            目标TIPS
          </Button>
        </Space>
      </div>

      {/* 统计卡 */}
      <div className="mod-stats">
        <div className="mod-stat-card">
          <div className="mod-stat-icon" style={{ background: 'rgba(114,46,209,0.12)', color: '#722ED1' }}>
            <AimOutlined />
          </div>
          <div>
            <div className="mod-stat-value">{totalGoals}</div>
            <div className="mod-stat-label">目标总数</div>
          </div>
        </div>
        <div className="mod-stat-card">
          <div className="mod-stat-icon" style={{ background: 'rgba(24,144,255,0.12)', color: '#1890FF' }}>
            <FireOutlined />
          </div>
          <div>
            <div className="mod-stat-value">{activeGoals}</div>
            <div className="mod-stat-label">进行中</div>
          </div>
        </div>
        <div className="mod-stat-card">
          <div className="mod-stat-icon" style={{ background: 'rgba(82,196,26,0.12)', color: '#52C41A' }}>
            <CheckCircleOutlined />
          </div>
          <div>
            <div className="mod-stat-value">{completedGoals}</div>
            <div className="mod-stat-label">已完成</div>
          </div>
        </div>
        <div className="mod-stat-card">
          <div className="mod-stat-icon" style={{ background: 'rgba(217,217,217,0.35)', color: '#8c8c8c' }}>
            <ClockCircleOutlined />
          </div>
          <div>
            <div className="mod-stat-value">{planningGoals}</div>
            <div className="mod-stat-label">规划中</div>
          </div>
        </div>
      </div>

      {/* 主要内容 */}
      <div>
        <Tabs
          activeKey={activeTab} 
          onChange={setActiveTab}
          items={[
            {
              key: 'goals',
              label: (
                <span>
                  <AimOutlined />
                  目标列表
                </span>
              ),
              children: (
                <Row gutter={[16, 16]}>
                  {goals.map((goal) => (
                    <Col xs={24} md={12} lg={8} key={goal.id}>
                      <Card
                        hoverable
                        style={{ height: '100%', borderRadius: '14px', boxShadow: '0 4px 16px rgba(15,23,42,0.06)', border: '1px solid #f0f0f5' }}
                        styles={{ header: { borderBottom: '1px solid #f5f5fa' } }}
                        title={
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                            <span style={{ fontWeight: 600 }}>{goal.title}</span>
                            <Space>
                              <Tag color={getStatusColor(goal.status)}>
                                {goalsConfig.goalStatus.find(s => s.key === goal.status)?.name}
                              </Tag>
                              <Tag color={getPriorityColor(goal.priority)}>
                                {goalsConfig.priorities.find(p => p.key === goal.priority)?.name}
                              </Tag>
                            </Space>
                          </div>
                        }
                        extra={
                          <Space>
                            <Button 
                              type="text" 
                              icon={<EyeOutlined />}
                              onClick={() => handleOpenProgress(goal)}
                              size="small"
                            />
                            <Button 
                              type="text" 
                              icon={<EditOutlined />}
                              onClick={() => handleOpenGoal(goal)}
                              size="small"
                            />
                            <Popconfirm
                              title="确定要删除这个目标吗？"
                              onConfirm={() => handleDeleteGoal(goal.id)}
                              okText="确定"
                              cancelText="取消"
                            >
                              <Button 
                                type="text" 
                                icon={<DeleteOutlined />}
                                size="small"
                                danger
                              />
                            </Popconfirm>
                          </Space>
                        }
                      >
                        <div style={{ marginBottom: '12px' }}>
                          <Text type="secondary">{goal.description}</Text>
                        </div>
                        
                        <div style={{ marginBottom: '12px' }}>
                          <Tag color={getTypeColor(goal.type)}>
                            {goalsConfig.goalTypes.find(t => t.key === goal.type)?.name}
                          </Tag>
                        </div>
                        
                        <div style={{ marginBottom: '12px' }}>
                          <Progress 
                            percent={calculateProgress(goal)}
                            size="small"
                            strokeColor={goalsConfig.bgColor}
                          />
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {goal.current_value || 0} / {goal.target_value}
                          </Text>
                        </div>
                        
                        <div style={{ marginBottom: '12px' }}>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            开始: {goal.start_date} | 结束: {goal.end_date}
                          </Text>
                        </div>
                        
                        {goal.plan && (
                          <div style={{ marginBottom: '12px' }}>
                            <Text strong>计划:</Text>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              {goal.plan}
                            </Text>
                          </div>
                        )}
                      </Card>
                    </Col>
                  ))}
                  
                  {goals.length === 0 && (
                    <Col span={24}>
                      <Card style={{ borderRadius: '14px', border: '1px solid #f0f0f5' }}>
                        <Empty
                          image={<AimOutlined style={{ fontSize: '56px', color: '#D9D9D9' }} />}
                          imageStyle={{ height: 64 }}
                          description={
                            <span style={{ color: '#8c8c8c' }}>
                              暂无目标，点击“新增目标”开始设定您的第一个目标
                            </span>
                          }
                          style={{ padding: '32px 0' }}
                        >
                          <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => handleOpenGoal()}
                            style={{ backgroundColor: goalsConfig.bgColor, borderColor: goalsConfig.bgColor }}
                          >
                            新增目标
                          </Button>
                        </Empty>
                      </Card>
                    </Col>
                  )}
                </Row>
              )
            }
          ]}
        />
      </div>

      {/* 目标弹窗 */}
      {showGoalModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 999999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '800px',
            maxWidth: '90vw',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600' }}>
              {selectedGoal ? '编辑目标' : '新增目标'}
            </h3>
        <Form
          form={goalForm}
          layout="vertical"
          onFinish={handleSaveGoal}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="title"
                label="目标标题"
                rules={[{ required: true, message: '请输入目标标题' }]}
              >
                <Input placeholder="请输入目标标题" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="type"
                label="目标类型"
                rules={[{ required: true, message: '请选择目标类型' }]}
              >
                <select
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'white'
                  }}
                  value={goalForm.getFieldValue('type') || ''}
                  onChange={(e) => goalForm.setFieldsValue({ type: e.target.value })}
                >
                  <option value="">请选择目标类型</option>
                  {goalsConfig.goalTypes.map(type => (
                    <option key={type.key} value={type.key}>
                      {type.icon} {type.name}
                    </option>
                  ))}
                </select>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="description"
            label="目标描述"
            rules={[{ required: true, message: '请输入目标描述' }]}
          >
            <TextArea rows={3} placeholder="请详细描述您的目标" />
          </Form.Item>
          
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="status"
                label="目标状态"
                rules={[{ required: true, message: '请选择目标状态' }]}
              >
                <select
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'white'
                  }}
                  value={goalForm.getFieldValue('status') || ''}
                  onChange={(e) => goalForm.setFieldsValue({ status: e.target.value })}
                >
                  <option value="">请选择状态</option>
                  {goalsConfig.goalStatus.map(status => (
                    <option key={status.key} value={status.key}>
                      {status.name}
                    </option>
                  ))}
                </select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="priority"
                label="优先级"
                rules={[{ required: true, message: '请选择优先级' }]}
              >
                <select
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'white'
                  }}
                  value={goalForm.getFieldValue('priority') || ''}
                  onChange={(e) => goalForm.setFieldsValue({ priority: e.target.value })}
                >
                  <option value="">请选择优先级</option>
                  {goalsConfig.priorities.map(priority => (
                    <option key={priority.key} value={priority.key}>
                      {priority.name}
                    </option>
                  ))}
                </select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="target_value"
                label="目标值"
                rules={[{ required: true, message: '请输入目标值' }]}
              >
                <Input placeholder="请输入目标值" />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="start_date"
                label="开始日期"
              >
                <DatePicker style={{ width: '100%' }} placeholder="选择开始日期" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="end_date"
                label="结束日期"
              >
                <DatePicker style={{ width: '100%' }} placeholder="选择结束日期" />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="plan"
            label="执行计划"
          >
            <TextArea rows={3} placeholder="请描述您的执行计划" />
          </Form.Item>
          
          <Form.Item
            name="evaluation"
            label="评价标准"
          >
            <TextArea rows={2} placeholder="请描述如何评价目标完成情况" />
          </Form.Item>
          
          <Form.Item
            name="reward"
            label="奖励设置"
          >
            <TextArea rows={2} placeholder="请描述完成目标后的奖励" />
          </Form.Item>
          
          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
              <Button onClick={() => setShowGoalModal(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                {selectedGoal ? '更新目标' : '创建目标'}
              </Button>
            </div>
          </Form.Item>
        </Form>
          </div>
        </div>
      )}

      {/* 进度弹窗 */}
      {showProgressModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 999999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '600px',
            maxWidth: '90vw',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600' }}>
              记录进度 - {selectedGoal?.title}
            </h3>
        <Form
          form={progressForm}
          layout="vertical"
          onFinish={handleSaveProgress}
        >
          <Form.Item
            name="content"
            label="进度内容"
            rules={[{ required: true, message: '请输入进度内容' }]}
          >
            <TextArea rows={4} placeholder="请详细描述您的进度情况" />
          </Form.Item>
          
          <Form.Item
            name="percentage"
            label="完成百分比"
            rules={[{ required: true, message: '请输入完成百分比' }]}
          >
            <Input type="number" min={0} max={100} placeholder="请输入0-100的数字" />
          </Form.Item>
          
          <Form.Item
            name="notes"
            label="备注"
          >
            <TextArea rows={2} placeholder="请添加备注信息" />
          </Form.Item>
          
          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
              <Button onClick={() => setShowProgressModal(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                保存进度
              </Button>
            </div>
          </Form.Item>
        </Form>
          </div>
        </div>
      )}

      {/* 目标TIPS弹窗 */}
      {showTipsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 999999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '800px',
            maxWidth: '90vw',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', display: 'flex', alignItems: 'center' }}>
              <BulbOutlined style={{ color: goalsConfig.bgColor, marginRight: '8px' }} />
              目标设定TIPS
            </h3>
        <div style={{ padding: '16px 0' }}>
          {/* SMART原则 */}
          <Card title="SMART目标设定原则" style={{ marginBottom: '16px' }}>
            <Row gutter={[16, 16]}>
              {goalsConfig.smartPrinciples.principles.map((principle, index) => (
                <Col xs={24} md={12} key={index}>
                  <div style={{ 
                    border: '1px solid #f0f0f0', 
                    borderRadius: '8px', 
                    padding: '16px',
                    marginBottom: '16px'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      marginBottom: '12px' 
                    }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: goalsConfig.bgColor,
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        marginRight: '12px'
                      }}>
                        {principle.letter}
                      </div>
                      <Title level={5} style={{ margin: 0 }}>
                        {principle.name}
                      </Title>
                    </div>
                    <Text type="secondary" style={{ display: 'block', marginBottom: '8px' }}>
                      {principle.description}
                    </Text>
                    <div style={{ 
                      backgroundColor: '#f6f6f6', 
                      padding: '8px', 
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}>
                      {principle.example}
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Card>

          {/* 目标设定TIPS */}
          <Card title="目标设定小TIPS" style={{ marginBottom: '16px' }}>
            <Timeline>
              {goalsConfig.goalSettingTips.tips.map((tip, index) => (
                <Timeline.Item key={index}>
                  <Title level={5}>{tip.title}</Title>
                  <Text type="secondary" style={{ display: 'block', marginBottom: '8px' }}>
                    {tip.description}
                  </Text>
                  <div style={{ 
                    backgroundColor: '#f6f6f6', 
                    padding: '8px', 
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}>
                    示例: {tip.example}
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          </Card>

          {/* 目标管理流程 */}
          <Card title="目标管理五大流程">
            <Steps direction="vertical" size="small">
              {goalsConfig.goalManagementProcess.process.map((step, index) => (
                <Step
                  key={index}
                  title={step.title}
                  description={step.description}
                  icon={<span style={{ fontSize: '16px' }}>{step.icon}</span>}
                />
              ))}
            </Steps>
          </Card>
        </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
              <Button onClick={() => setShowTipsModal(false)}>
                关闭
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Goals;
